import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import Button from './Button'
import theme from '../styles/theme'
import { getUserOffers, getUserUsedOffers, issuePerkWithCredit, removeOfferWithCredit, AVAILABLE_OFFERS, AVAILABLE_OFFER_ROOT_IDS, issueGiftCard, issueCustomGiftCard, lookupGiftCardsByEmail, deactivateGiftCard, getMarianaTekGiftCardInstances, lookupReceivedGiftCards, lookupDiscountCodes, deactivateDiscountCode, resendGiftCardNotification, syncCreditToMT } from '../services/api'
import { useAuditLog, AUDIT_ACTIONS } from '../services/auditLog'
import { useAuth } from './auth/AuthProvider'
import UserNotesModal from './UserNotesModal'

const OffersContainer = styled.div`
  background: ${theme.color.background.light};
  padding: 2rem;
  border-radius: 8px;
  box-shadow: inset 0 2px 4px ${theme.color.shadow.light};
`

const OffersHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  gap: 2rem;
  flex-wrap: wrap;
`

const Title = styled.h2`
  font-size: 2.4rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin: 0;
`

const UserNotesButton = styled(Button)`
  background: #fefacd;
  color: #444;
  border: 2px solid #d4a017;
  font-weight: 600;

  &:hover:not([disabled]) {
    background: #fef9b8;
    border-color: #b8860b;
  }
`

const CurrentOffersSection = styled.div`
  margin-bottom: 3rem;
`

const SectionTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: 600;
  color: ${theme.color.text.base};
  margin-bottom: 1rem;
`

const OffersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const OfferItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: ${theme.color.background.lightGrey};
  border-radius: 4px;
  border-left: 4px solid ${theme.color.accent.primary};
`

const OfferInfo = styled.div`
  flex: 1;
`

const OfferName = styled.div`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${theme.color.text.base};
  margin-bottom: 0.25rem;
`

const OfferDetails = styled.div`
  font-size: 1.2rem;
  color: ${theme.color.text.grey};
`

const SystemBadge = styled.span<{ system: 'sm' | 'mt' }>`
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 3px;
  font-size: 1rem;
  font-weight: 700;
  margin-left: 0.4rem;
  background: ${props => props.system === 'sm' ? '#e8f0fe' : '#fef3e0'};
  color: ${props => props.system === 'sm' ? '#1a73e8' : '#e65100'};
`

const OfferQuantity = styled.div`
  background: ${theme.color.accent.primary};
  color: ${theme.color.text.light};
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 1.2rem;
  font-weight: 600;
  min-width: 40px;
  text-align: center;
`

const OfferActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const RemoveButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;

  &:hover:not([disabled]) {
    background: #c82333;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const RemoveQuantitySelect = styled.select`
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  border: 1px solid ${theme.color.border.light};
  font-size: 1.1rem;
`

const IssueOffersContainer = styled.div`
  border-top: 1px solid ${theme.color.border.light};
  padding-top: 2rem;
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
`

const IssueOffersSection = styled.div`
  flex: 1;
  min-width: 300px;
`

const IssueForm = styled.form`
  display: flex;
  gap: 1rem;
  align-items: end;
  flex-wrap: wrap;
`

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 200px;
`

const Label = styled.label`
  font-size: 1.2rem;
  font-weight: 600;
  color: ${theme.color.text.grey};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`

const Select = styled.select`
  padding: 1rem;
  border: 2px solid ${theme.color.border.light};
  border-radius: 4px;
  font-size: 1.4rem;
  background: white;

  &:focus {
    outline: none;
    border-color: ${theme.color.accent.primary};
  }
`

const Input = styled.input`
  padding: 1rem;
  border: 2px solid ${theme.color.border.light};
  border-radius: 4px;
  font-size: 1.4rem;
  background: white;

  &:focus {
    outline: none;
    border-color: ${theme.color.accent.primary};
  }
`

const TextArea = styled.textarea`
  padding: 1rem;
  border: 2px solid ${theme.color.border.light};
  border-radius: 4px;
  font-size: 1.4rem;
  background: white;
  resize: vertical;
  min-height: 60px;

  &:focus {
    outline: none;
    border-color: ${theme.color.accent.primary};
  }
`

const ExampleText = styled.div`
  font-size: 1.1rem;
  color: ${theme.color.text.grey};
  font-style: italic;
  margin-top: 0.25rem;
`

const AmountInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`

const CurrencySymbol = styled.span`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${theme.color.text.base};
`

const NoOffersMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${theme.color.text.grey};
  font-style: italic;
`

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${theme.color.text.grey};
`

const ErrorMessage = styled.div`
  background: #ffe6e6;
  color: #d70036;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
`

const SuccessMessage = styled.div`
  background: #e6ffe6;
  color: #007700;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
`

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`

const ModalContent = styled.div`
  background: ${theme.color.background.light};
  border-radius: 8px;
  padding: 2rem;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
`

const ModalTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: 600;
  color: ${theme.color.text.base};
  margin-bottom: 1.5rem;
`

const ModalButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`

const GiftCardHistorySection = styled.div`
  border-top: 1px solid ${theme.color.border.light};
  padding-top: 2rem;
  margin-top: 2rem;
`

const GiftCardHistoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`

const RefreshButton = styled.button`
  background: none;
  border: 1px solid ${theme.color.border.light};
  border-radius: 4px;
  padding: 0.5rem 1rem;
  font-size: 1.2rem;
  color: ${theme.color.text.grey};
  cursor: pointer;

  &:hover {
    border-color: ${theme.color.accent.primary};
    color: ${theme.color.accent.primary};
  }
`

const GiftCardTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 1.3rem;
`

const TableHeader = styled.th`
  text-align: left;
  padding: 0.75rem 1rem;
  font-weight: 600;
  color: ${theme.color.text.grey};
  font-size: 1.1rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 2px solid ${theme.color.border.light};
`

const TableRow = styled.tr`
  &:nth-child(even) {
    background: ${theme.color.background.lightGrey};
  }
`

const TableCell = styled.td`
  padding: 0.75rem 1rem;
  color: ${theme.color.text.base};
  border-bottom: 1px solid ${theme.color.border.light};
`

const StatusBadge = styled.span<{ active: boolean }>`
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 10px;
  font-size: 1.1rem;
  font-weight: 600;
  background: ${props => props.active ? '#e6ffe6' : '#ffe6e6'};
  color: ${props => props.active ? '#007700' : '#d70036'};
`

const CodeText = styled.span`
  font-family: monospace;
  font-size: 1.3rem;
  color: #007700;
  font-weight: 600;
`

const MILESTONE_DATA: Record<string, { classes: number, value: number }> = {
  '250': { classes: 250, value: 50 },
  '500': { classes: 500, value: 100 },
  '750': { classes: 750, value: 150 },
  '1000': { classes: 1000, value: 200 },
  '1500': { classes: 1500, value: 250 },
  '2000': { classes: 2000, value: 300 },
  '2500': { classes: 2500, value: 350 },
  '3000': { classes: 3000, value: 400 },
  '3500': { classes: 3500, value: 450 },
  '4000': { classes: 4000, value: 500 },
  '4500': { classes: 4500, value: 500 },
  '5000': { classes: 5000, value: 500 },
  '5500': { classes: 5500, value: 500 },
  '6000': { classes: 6000, value: 500 },
  '6500': { classes: 6500, value: 500 },
  '7000': { classes: 7000, value: 500 },
  '7500': { classes: 7500, value: 500 }
}

const getGiftCardMilestones = (currencySymbol: string) => {
  const milestones: Record<string, { label: string, value: string }> = {}
  for (const [key, data] of Object.entries(MILESTONE_DATA)) {
    milestones[key] = {
      label: `${data.classes} classes - ${currencySymbol}${data.value}`,
      value: data.value.toFixed(1)
    }
  }
  return milestones
}

interface OffersSectionProps {
  userData: any
  onUpdate: () => void
}

const maskCode = (code: string | undefined, isSuperAdmin: boolean): string => {
  if (!code) return '-'
  if (isSuperAdmin) return code
  if (code.length <= 4) return code
  return '****' + code.slice(-4)
}

const canonicalOfferNameForRemoval = (offer: any): string | null => {
  const offerId = typeof offer?.offer_id === 'string' ? offer.offer_id : ''
  const rootOfferId = typeof offer?.root_offer_id === 'string' ? offer.root_offer_id : ''
  if (offerId || rootOfferId) {
    const matches = new Set([
      ...Object.entries(AVAILABLE_OFFERS),
      ...Object.entries(AVAILABLE_OFFER_ROOT_IDS),
    ]
      .filter(([, allowlistedId]) => allowlistedId === offerId || allowlistedId === rootOfferId)
      .map(([offerName]) => offerName))
    return matches.size === 1 ? Array.from(matches)[0] : null
  }

  const displayName = typeof offer?.name === 'string' ? offer.name : ''
  return Object.prototype.hasOwnProperty.call(AVAILABLE_OFFERS, displayName)
    ? displayName
    : null
}

const OffersSection: React.FC<OffersSectionProps> = ({ userData, onUpdate }) => {
  const { logEvent } = useAuditLog()
  const { isAdmin, isSuperAdmin, isUK, isDublin, user: authUser } = useAuth()
  const adminEmail = (authUser?.email || '').toLowerCase()
  const [offers, setOffers] = useState<any[]>([])
  const [usedOffers, setUsedOffers] = useState<any[]>([])
  const [isLoadingOffers, setIsLoadingOffers] = useState(false)
  const [isLoadingUsedOffers, setIsLoadingUsedOffers] = useState(false)
  const [selectedOffer, setSelectedOffer] = useState<string>('')
  const [isIssuingOffer, setIsIssuingOffer] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)
  const [removingOfferName, setRemovingOfferName] = useState<string | null>(null)
  const [removeQuantities, setRemoveQuantities] = useState<Record<string, number>>({})

  // Gift Card state
  const [giftCardMode, setGiftCardMode] = useState<'milestone' | 'custom'>('milestone')
  const [selectedMilestone, setSelectedMilestone] = useState<string>('')
  const [customAmount, setCustomAmount] = useState<string>('')
  const [customNote, setCustomNote] = useState<string>('')
  const [isIssuingGiftCard, setIsIssuingGiftCard] = useState(false)
  const [showGiftCardConfirmation, setShowGiftCardConfirmation] = useState(false)
  const [giftCardMessage, setGiftCardMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)

  // Currency selection for gift cards
  const [giftCardCurrency, setGiftCardCurrency] = useState<'USD' | 'GBP' | 'EUR'>('USD')
  const currencySymbol = giftCardCurrency === 'EUR' ? '€' : giftCardCurrency === 'GBP' ? '£' : '$'
  const GiftCardMilestones = getGiftCardMilestones(currencySymbol)

  // User Notes state
  const [showUserNotes, setShowUserNotes] = useState(false)

  // Gift Card History state
  const [giftCardHistory, setGiftCardHistory] = useState<any[]>([])
  const [isLoadingGiftCardHistory, setIsLoadingGiftCardHistory] = useState(false)
  const [deactivatingGiftCardId, setDeactivatingGiftCardId] = useState<string | number | null>(null)

  // MT Gift Card state
  const [mtGiftCards, setMtGiftCards] = useState<any[]>([])
  const [isLoadingMtGiftCards, setIsLoadingMtGiftCards] = useState(false)

  // Syncing state
  const [syncingOffer, setSyncingOffer] = useState<string | null>(null)

  // Received MT Gift Cards (from Athena)
  const [receivedGiftCards, setReceivedGiftCards] = useState<any[]>([])
  const [isLoadingReceivedGiftCards, setIsLoadingReceivedGiftCards] = useState(false)

  // Fuel Bar Discount Codes (from DynamoDB)
  const [discountCodes, setDiscountCodes] = useState<any[]>([])
  const [isLoadingDiscountCodes, setIsLoadingDiscountCodes] = useState(false)
  const [deactivatingDiscountCode, setDeactivatingDiscountCode] = useState<string | null>(null)
  const [resendingGiftCardId, setResendingGiftCardId] = useState<string | number | null>(null)

  // MT Active Credits (for cross-referencing with SessionM offers)
  const [mtActiveCredits, setMtActiveCredits] = useState<any[]>([])

  // Mapping: MT credit ID → keywords to match SM offer names
  const MT_CREDIT_TO_OFFER: Record<string, { name: string; keywords: string[] }> = {
    '1977': { name: 'Cancellation Forgiveness', keywords: ['cancellation'] },
    '1976': { name: 'Complimentary Guest Pass', keywords: ['guest pass'] },
    '1975': { name: 'Birthday Credit', keywords: ['birthday'] },
    '1230': { name: "25% Off Barry's Shop", keywords: ['25%', '25% off'] },
  }

  useEffect(() => {
    if (userData?.user?.id) {
      loadUserOffers()
      loadUsedOffers()
    }
    if (userData?.user?.email) {
      loadGiftCardHistory()
    }
    const marianaTekId = userData?.user?.external_id || userData?.external_ids?.mariana_tek
    if (marianaTekId) {
      loadMtGiftCards(marianaTekId)
      loadMtActiveCredits(marianaTekId)
    }
    if (userData?.user?.email) {
      loadReceivedGiftCards()
      loadDiscountCodes()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.user?.id])

  const loadUserOffers = async () => {
    if (!userData?.user?.id) return

    setIsLoadingOffers(true)
    try {
      const offersData = await getUserOffers(userData.user.id)
      console.log('Offers API response:', offersData)
      // getUserOffers now returns an array directly
      setOffers(Array.isArray(offersData) ? offersData : [])
    } catch (err) {
      console.error('Failed to load offers:', err)
      setOffers([])
    } finally {
      setIsLoadingOffers(false)
    }
  }

  const loadMtActiveCredits = async (marianaTekId: string) => {
    try {
      const response = await fetch(`/api/marianatek/api/credit_transactions/?user=${encodeURIComponent(marianaTekId)}&active=true`)
      if (response.ok) {
        const data = await response.json()
        const credits = (data?.data || []).filter((c: any) =>
          c.attributes?.remaining_credits_cache > 0 && !c.attributes?.is_expired
        )
        console.log('MT active credits:', credits.map((c: any) => ({
          id: c.id,
          credit_id: c.relationships?.credit?.data?.id,
          name: c.attributes?.credit_name,
          remaining: c.attributes?.remaining_credits_cache
        })))
        setMtActiveCredits(credits)
      }
    } catch (err) {
      console.error('Failed to load MT active credits:', err)
    }
  }

  const handleSyncToMT = async (offerName: string, expirationDate?: string) => {
    const marianaTekId = userData?.user?.external_id || userData?.external_ids?.mariana_tek
    if (!marianaTekId) {
      setMessage({ type: 'error', text: 'No Mariana Tek ID found for this user' })
      return
    }

    setSyncingOffer(offerName)
    setMessage(null)

    try {
      await syncCreditToMT(userData.user.email, offerName, expirationDate)
      setMessage({ type: 'success', text: `${offerName} synced to Mariana Tek successfully` })
      // Refresh MT credits to update badges
      loadMtActiveCredits(marianaTekId)
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to sync to MT' })
    } finally {
      setSyncingOffer(null)
    }
  }

  const loadUsedOffers = async () => {
    if (!userData?.user?.id) return
    setIsLoadingUsedOffers(true)
    try {
      const data = await getUserUsedOffers(userData.user.id)
      setUsedOffers(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error('Failed to load used offers:', err)
      setUsedOffers([])
    } finally {
      setIsLoadingUsedOffers(false)
    }
  }

  const loadGiftCardHistory = async () => {
    if (!userData?.user?.email) return
    setIsLoadingGiftCardHistory(true)
    try {
      const result = await lookupGiftCardsByEmail(userData.user.email)
      setGiftCardHistory(result.giftCards || [])
    } catch (err) {
      console.error('Failed to load gift card history:', err)
      setGiftCardHistory([])
    } finally {
      setIsLoadingGiftCardHistory(false)
    }
  }

  const handleDeactivateGiftCard = async (gcId: string | number, currency?: string) => {
    if (!window.confirm('Are you sure you want to deactivate this gift card? This cannot be undone.')) return
    setDeactivatingGiftCardId(gcId)
    try {
      await deactivateGiftCard(gcId, currency, adminEmail)
      setGiftCardHistory(prev => prev.map(gc =>
        gc.id === gcId ? { ...gc, status: 'disabled' } : gc
      ))
    } catch (err: any) {
      console.error('Failed to deactivate gift card:', err)
      alert(`Failed to deactivate: ${err.message}`)
    } finally {
      setDeactivatingGiftCardId(null)
    }
  }

  const loadMtGiftCards = async (marianaTekId?: string) => {
    const mtId = marianaTekId || userData?.user?.external_id || userData?.external_ids?.mariana_tek
    if (!mtId) return
    setIsLoadingMtGiftCards(true)
    try {
      const result = await getMarianaTekGiftCardInstances(mtId)
      setMtGiftCards(result.data || [])
    } catch (err) {
      console.error('Failed to load MT gift cards:', err)
      setMtGiftCards([])
    } finally {
      setIsLoadingMtGiftCards(false)
    }
  }

  const loadReceivedGiftCards = async () => {
    if (!userData?.user?.email) return
    setIsLoadingReceivedGiftCards(true)
    try {
      const result = await lookupReceivedGiftCards(userData.user.email)
      setReceivedGiftCards(result.giftCards || [])
    } catch (err) {
      console.error('Failed to load received gift cards:', err)
      setReceivedGiftCards([])
    } finally {
      setIsLoadingReceivedGiftCards(false)
    }
  }

  const loadDiscountCodes = async () => {
    if (!userData?.user?.email) return
    setIsLoadingDiscountCodes(true)
    try {
      const result = await lookupDiscountCodes(userData.user.email)
      setDiscountCodes(result.discountCodes || [])
    } catch (err) {
      console.error('Failed to load discount codes:', err)
      setDiscountCodes([])
    } finally {
      setIsLoadingDiscountCodes(false)
    }
  }

  const handleDeactivateDiscountCode = async (code: string) => {
    if (!window.confirm(`Are you sure you want to force deactivate discount code ${code}? This will deactivate it in Mariana Tek, update DynamoDB, untag the user in Klaviyo, and revoke the SessionM offer.`)) return
    setDeactivatingDiscountCode(code)
    try {
      await deactivateDiscountCode(code, userData.user.email)
      await logEvent({
        tabName: 'Discount Codes',
        action: AUDIT_ACTIONS.OFFER_ISSUED,
        details: {
          action: 'discount_code_deactivated',
          discount_code: code
        },
        targetUserId: userData.user.id,
        targetUserEmail: userData.user?.email
      })
      setDiscountCodes(prev => prev.map(dc =>
        dc.discount_code === code ? { ...dc, status: 'used' } : dc
      ))
    } catch (err: any) {
      console.error('Failed to deactivate discount code:', err)
      alert(`Failed to deactivate: ${err.message}`)
    } finally {
      setDeactivatingDiscountCode(null)
    }
  }

  const handleResendGiftCardNotification = async (gcId: string | number, currency?: string) => {
    if (!window.confirm('Resend gift card notification email to the customer?')) return
    setResendingGiftCardId(gcId)
    try {
      await resendGiftCardNotification(gcId, userData.user.email, currency)
      // Resend is audited by the server from the verified actor and owned card.

      alert('Gift card notification sent successfully!')
    } catch (err: any) {
      console.error('Failed to resend gift card notification:', err)
      alert(`Failed to resend: ${err.message}`)
    } finally {
      setResendingGiftCardId(null)
    }
  }

  const handleIssueOffer = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedOffer || !userData?.user?.id) return

    setIsIssuingOffer(true)
    setMessage(null)

    try {
      // Issue the selected Perk and its matching Mariana Tek credit when required.
      // The protected server path records the authoritative audit entry.
      await issuePerkWithCredit(userData.user.email, selectedOffer)

      setMessage({ type: 'success', text: 'Perk issued successfully' })
      setSelectedOffer('')
      await Promise.all([loadUserOffers(), loadDiscountCodes()])

      // Refresh user data with error handling
      try {
        await onUpdate()
      } catch (updateErr) {
        console.error('Failed to refresh user data after issuing offer:', updateErr)
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to issue Perk'
      })
    } finally {
      setIsIssuingOffer(false)
    }
  }

  const handleRemoveOffer = async (groupedOffer: any) => {
    if (!userData?.user?.id) return
    const qty = removeQuantities[groupedOffer.name] || 1
    if (!window.confirm(`Remove ${qty} "${groupedOffer.name}" offer${qty > 1 ? 's' : ''}?`)) return

    setRemovingOfferName(groupedOffer.name)
    setMessage(null)

    try {
      const marianaTekId = userData?.user?.external_id || userData?.external_ids?.mariana_tek
      const instancesToRemove = groupedOffer.instances.slice(0, qty)
      const removalRequests = instancesToRemove.map((instance: any) => {
        const canonicalOfferName = canonicalOfferNameForRemoval(instance)
        if (!canonicalOfferName) {
          throw new Error('This offer cannot be removed because its offer type is not supported')
        }
        const offerInstanceId = typeof instance.offer_instance_id === 'string'
          ? instance.offer_instance_id
          : ''
        if (!offerInstanceId) {
          throw new Error('This offer cannot be removed because its instance ID is missing')
        }
        return { canonicalOfferName, offerInstanceId }
      })

      for (const request of removalRequests) {
        await removeOfferWithCredit(userData.user.email, request.canonicalOfferName, request.offerInstanceId)
      }

      await logEvent({
        tabName: 'Current Offers',
        action: AUDIT_ACTIONS.OFFER_ISSUED,
        details: {
          action: 'removed',
          offer_name: groupedOffer.name,
          quantity_removed: qty,
          mariana_tek_id: marianaTekId
        },
        targetUserId: userData.user.id,
        targetUserEmail: userData.user?.email
      })

      setMessage({ type: 'success', text: `${qty} "${groupedOffer.name}" removed successfully` })
      setRemoveQuantities(prev => { const next = { ...prev }; delete next[groupedOffer.name]; return next })
      await loadUserOffers()

      try {
        await onUpdate()
      } catch (updateErr) {
        console.error('Failed to refresh user data after removing offer:', updateErr)
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to remove offer'
      })
    } finally {
      setRemovingOfferName(null)
    }
  }

  const groupOffersByName = (offers: any[]) => {
    const grouped = offers.reduce((acc, offer: any) => {
      const name = offer.name || 'Unknown Offer'
      if (!acc[name]) {
        acc[name] = {
          ...offer,
          quantity: 0,
          instances: []
        }
      }
      acc[name].quantity += 1
      acc[name].instances.push(offer)
      return acc
    }, {} as Record<string, any>)

    return Object.values(grouped)
  }

  const formatExpirationDate = (dateString: string) => {
    if (!dateString) return 'No expiration'
    try {
      return new Date(dateString).toLocaleDateString()
    } catch {
      return dateString
    }
  }

  const handleGiftCardClick = () => {
    if (giftCardMode === 'milestone') {
      if (!selectedMilestone) return
    } else {
      const amount = parseFloat(customAmount)
      if (!customAmount || isNaN(amount) || amount <= 0) return
      if (amount > 500 && !isSuperAdmin) {
        setGiftCardMessage({
          type: 'error',
          text: 'Only Super Admin and above can issue gift cards greater than 500'
        })
        return
      }
    }
    setGiftCardMessage(null)
    setShowGiftCardConfirmation(true)
  }

  const handleConfirmGiftCard = async () => {
    if (!userData?.user) return

    setIsIssuingGiftCard(true)
    setGiftCardMessage(null)
    setShowGiftCardConfirmation(false)

    try {
      const email = userData.user.email
      const firstName = userData.user.first_name || ''
      const lastName = userData.user.last_name || ''

      let result: any

      if (giftCardMode === 'milestone') {
        if (!selectedMilestone) return
        const numClasses = parseInt(selectedMilestone)
        result = await issueGiftCard(email, firstName, lastName, numClasses, giftCardCurrency)

        await logEvent({
          tabName: 'Current Offers',
          action: AUDIT_ACTIONS.GIFT_CARD_ISSUED,
          details: {
            milestone: selectedMilestone,
            gift_card_code_last4: result.giftCard?.code ? String(result.giftCard.code).slice(-4) : null,
            gift_card_balance: result.giftCard?.balance
          },
          targetUserId: userData.user.id,
          targetUserEmail: email
        })

        setSelectedMilestone('')
      } else {
        const amount = parseFloat(customAmount)
        result = await issueCustomGiftCard(email, firstName, lastName, amount, customNote, giftCardCurrency, adminEmail)

        await logEvent({
          tabName: 'Current Offers',
          action: AUDIT_ACTIONS.GIFT_CARD_ISSUED,
          details: {
            type: 'custom',
            amount: customAmount,
            note: customNote,
            currency: giftCardCurrency,
            gift_card_code_last4: result.giftCard?.code ? String(result.giftCard.code).slice(-4) : null,
            gift_card_balance: result.giftCard?.balance
          },
          targetUserId: userData.user.id,
          targetUserEmail: email
        })

        setCustomAmount('')
        setCustomNote('')
      }

      setGiftCardMessage({
        type: 'success',
        text: `Gift card issued successfully! Code: ${maskCode(result.giftCard?.code, isSuperAdmin)}`
      })
    } catch (err) {
      setGiftCardMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to issue gift card'
      })
    } finally {
      setIsIssuingGiftCard(false)
    }
  }

  const handleCancelGiftCard = () => {
    setShowGiftCardConfirmation(false)
  }

  if (!userData || !userData.user) {
    return null
  }

  // Build a set of MT credit IDs the user currently has
  const mtCreditIdSet = new Set(
    mtActiveCredits.map((c: any) => c.relationships?.credit?.data?.id).filter(Boolean)
  )

  // Check if an SM offer name matches any MT credit via keywords
  const offerMatchesMtCredit = (offerName: string): string | null => {
    const lower = (offerName || '').toLowerCase()
    for (const [creditId, config] of Object.entries(MT_CREDIT_TO_OFFER)) {
      if (config.keywords.some(kw => lower.includes(kw))) return creditId
    }
    return null
  }

  // Does the SM offer have a matching active MT credit?
  const offerInMt = (offerName: string): boolean => {
    const creditId = offerMatchesMtCredit(offerName)
    return creditId !== null && mtCreditIdSet.has(creditId)
  }

  // Can this SM offer be synced to MT? (has a mapping but not currently in MT)
  const canSyncToMt = (offerName: string): boolean => {
    const creditId = offerMatchesMtCredit(offerName)
    return creditId !== null && !mtCreditIdSet.has(creditId)
  }

  // Get the MT offer name to pass to the sync endpoint
  const getMtOfferName = (smOfferName: string): string => {
    const creditId = offerMatchesMtCredit(smOfferName)
    if (creditId && MT_CREDIT_TO_OFFER[creditId]) return MT_CREDIT_TO_OFFER[creditId].name
    return smOfferName
  }

  // Find MT credits that have NO matching SessionM offer (MT-only)
  const smOfferNamesLower = offers.map((o: any) => (o.name || '').toLowerCase())
  const mtOnlyCredits = mtActiveCredits.filter((c: any) => {
    const creditId = c.relationships?.credit?.data?.id
    const config = MT_CREDIT_TO_OFFER[creditId]
    if (!config) return false // Skip unmapped credits (class packs etc)
    // Check if any SM offer matches this credit's keywords
    return !smOfferNamesLower.some(smName => config.keywords.some(kw => smName.includes(kw)))
  })

  const groupedOffers = groupOffersByName(offers)

  return (
    <OffersContainer>
      <OffersHeader>
        <Title>Current Offers</Title>
        <UserNotesButton onClick={() => setShowUserNotes(true)}>
          User Notes
        </UserNotesButton>
      </OffersHeader>

      <CurrentOffersSection>
        <SectionTitle>Active Offers</SectionTitle>

        {isLoadingOffers ? (
          <LoadingMessage>Loading offers...</LoadingMessage>
        ) : (groupedOffers.length > 0 || mtOnlyCredits.length > 0) ? (
          <OffersList>
            {(groupedOffers as any[]).map((offer: any, index) => (
              <OfferItem key={`sm-${index}`}>
                <OfferInfo>
                  <OfferName>
                    {offer.name}
                    <SystemBadge system="sm">SM</SystemBadge>
                    {offerInMt(offer.name) && <SystemBadge system="mt">MT</SystemBadge>}
                  </OfferName>
                  <OfferDetails>
                    Expires: {formatExpirationDate(offer.expiration_date)}
                  </OfferDetails>
                </OfferInfo>
                <OfferActions>
                  <OfferQuantity>{offer.quantity}</OfferQuantity>
                  {isSuperAdmin && canSyncToMt(offer.name) && (
                    <RemoveButton
                      style={{ background: '#e65100' }}
                      onClick={() => handleSyncToMT(getMtOfferName(offer.name), offer.expiration_date)}
                      disabled={syncingOffer === offer.name}
                    >
                      {syncingOffer === offer.name ? 'Syncing...' : 'Sync to MT'}
                    </RemoveButton>
                  )}
                  {isSuperAdmin && (
                    <>
                      <RemoveQuantitySelect
                        value={removeQuantities[offer.name] || 1}
                        onChange={(e) => setRemoveQuantities(prev => ({ ...prev, [offer.name]: parseInt(e.target.value) }))}
                        disabled={removingOfferName === offer.name}
                      >
                        {Array.from({ length: offer.quantity }, (_, i) => (
                          <option key={i + 1} value={i + 1}>{i + 1}</option>
                        ))}
                      </RemoveQuantitySelect>
                      <RemoveButton
                        onClick={() => handleRemoveOffer(offer)}
                        disabled={removingOfferName === offer.name}
                      >
                        {removingOfferName === offer.name ? 'Removing...' : 'Remove'}
                      </RemoveButton>
                    </>
                  )}
                </OfferActions>
              </OfferItem>
            ))}
            {mtOnlyCredits.map((credit: any) => {
              const creditId = credit.relationships?.credit?.data?.id
              const name = MT_CREDIT_TO_OFFER[creditId]?.name || credit.attributes?.credit_name || 'Unknown Credit'
              const remaining = credit.attributes?.remaining_credits_cache || 0
              const expires = credit.attributes?.expiration_datetime
              return (
                <OfferItem key={`mt-${credit.id}`}>
                  <OfferInfo>
                    <OfferName>
                      {name}
                      <SystemBadge system="mt">MT Only</SystemBadge>
                    </OfferName>
                    <OfferDetails>
                      Expires: {formatExpirationDate(expires)} | MT Credit ID: {creditId}
                    </OfferDetails>
                  </OfferInfo>
                  <OfferActions>
                    <OfferQuantity>{remaining}</OfferQuantity>
                  </OfferActions>
                </OfferItem>
              )
            })}
          </OffersList>
        ) : (
          <NoOffersMessage>No active offers found</NoOffersMessage>
        )}
      </CurrentOffersSection>

      <CurrentOffersSection>
        <SectionTitle>Used Offers</SectionTitle>
        {isLoadingUsedOffers ? (
          <LoadingMessage>Loading used offers...</LoadingMessage>
        ) : usedOffers.length > 0 ? (
          <GiftCardTable>
            <thead>
              <tr>
                <TableHeader>Offer</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Date</TableHeader>
              </tr>
            </thead>
            <tbody>
              {usedOffers.map((offer: any, index: number) => {
                const date = offer.redemption_date || offer.expiration_date
                const status = (offer.status || '').toLowerCase()
                return (
                  <TableRow key={offer.offer_instance_id || index}>
                    <TableCell>{offer.name}</TableCell>
                    <TableCell>
                      <StatusBadge active={false}>
                        {status === 'redeemed' ? 'Redeemed' : 'Expired'}
                      </StatusBadge>
                    </TableCell>
                    <TableCell>{date ? new Date(date).toLocaleDateString() : '-'}</TableCell>
                  </TableRow>
                )
              })}
            </tbody>
          </GiftCardTable>
        ) : (
          <NoOffersMessage>No used offers found</NoOffersMessage>
        )}
      </CurrentOffersSection>

      <CurrentOffersSection>
        <SectionTitle>Discount Codes</SectionTitle>

        {(() => {
          const shopOfferIds = [
            'a9836764-b389-4205-94e3-ff03d35f23d9', // Complimentary Shipping 1x Use
            '09edf5ab-da81-455c-9181-318174a0cbfe', // 25% Off Barry's Shop
          ]
          const shopOfferNames = ['shipping', '25% off']
          const shopOffers = offers.filter((o: any) =>
            shopOfferIds.includes(o.offer_id) ||
            shopOfferNames.some(n => (o.name || '').toLowerCase().includes(n))
          )
          const hasAnyData = discountCodes.length > 0 || shopOffers.length > 0
          const isLoading = isLoadingDiscountCodes || isLoadingOffers

          if (isLoading) {
            return <LoadingMessage>Loading discount codes...</LoadingMessage>
          }

          if (!hasAnyData) {
            return <NoOffersMessage>No active discount codes found</NoOffersMessage>
          }

          return (
            <GiftCardTable>
              <thead>
                <tr>
                  <TableHeader>Code / Offer ID</TableHeader>
                  <TableHeader>Type</TableHeader>
                  <TableHeader>Value</TableHeader>
                  <TableHeader>Status</TableHeader>
                  <TableHeader>Created</TableHeader>
                  {isSuperAdmin && <TableHeader>Actions</TableHeader>}
                </tr>
              </thead>
              <tbody>
                {shopOffers.map((offer: any, index: number) => (
                  <TableRow key={`offer-${offer.offer_instance_id || index}`}>
                    <TableCell><CodeText>{offer.offer_instance_id || '-'}</CodeText></TableCell>
                    <TableCell>{offer.name}</TableCell>
                    <TableCell>
                      {offer.name?.includes('25%') ? '25% Off' :
                       offer.name?.toLowerCase().includes('unlimited') ? 'Free Shipping (Unlimited)' :
                       'Free Shipping (1x)'}
                    </TableCell>
                    <TableCell>
                      <StatusBadge active={true}>active</StatusBadge>
                    </TableCell>
                    <TableCell>{offer.acquire_date ? new Date(offer.acquire_date).toLocaleDateString() : '-'}</TableCell>
                    {isSuperAdmin && <TableCell><span style={{ color: '#999', fontSize: '1.1rem' }}>-</span></TableCell>}
                  </TableRow>
                ))}
                {discountCodes.map((dc: any, index: number) => (
                  <TableRow key={dc.discount_code || index}>
                    <TableCell><CodeText>{dc.discount_code || `****${dc.code_last4 || ''}`}</CodeText></TableCell>
                    <TableCell>
                      {dc.discount_type === 'Free Fuel Bar'
                        ? 'Free Fuel Bar'
                        : dc.discount_type === 'percentage'
                          ? 'Percentage'
                          : 'Fixed Amount'}
                    </TableCell>
                    <TableCell>
                      {dc.discount_type === 'Free Fuel Bar'
                        ? '100% off F&B'
                        : dc.discount_type === 'percentage'
                          ? `${dc.discount_value}%`
                          : `$${dc.discount_value.toFixed(2)}`}
                    </TableCell>
                    <TableCell>
                      <StatusBadge active={dc.status === 'active'}>
                        {dc.status}
                      </StatusBadge>
                    </TableCell>
                    <TableCell>{dc.created_at ? new Date(dc.created_at).toLocaleDateString() : '-'}</TableCell>
                    {isSuperAdmin && (
                      <TableCell>
                        {dc.status === 'active' ? (
                          <button
                            onClick={() => handleDeactivateDiscountCode(dc.discount_code)}
                            disabled={deactivatingDiscountCode === dc.discount_code}
                            style={{
                              background: '#c33',
                              color: 'white',
                              border: 'none',
                              padding: '0.4rem 0.8rem',
                              borderRadius: '4px',
                              fontSize: '1.1rem',
                              fontWeight: 600,
                              cursor: 'pointer',
                              opacity: deactivatingDiscountCode === dc.discount_code ? 0.5 : 1,
                            }}
                          >
                            {deactivatingDiscountCode === dc.discount_code ? 'Deactivating...' : 'Force Deactivate'}
                          </button>
                        ) : (
                          <span style={{ color: '#999', fontSize: '1.1rem' }}>-</span>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </tbody>
            </GiftCardTable>
          )
        })()}
      </CurrentOffersSection>

      <IssueOffersContainer>
        {isAdmin && (
        <IssueOffersSection>
          <SectionTitle>Issue New Perk</SectionTitle>
          <IssueForm onSubmit={handleIssueOffer}>
            <FormGroup>
              <Label htmlFor="offerSelect">Select Perk</Label>
              <Select
                id="offerSelect"
                value={selectedOffer}
                onChange={(e) => setSelectedOffer(e.target.value)}
              >
                <option value="">Choose a perk...</option>
                {Object.keys(AVAILABLE_OFFERS).map(offerName => (
                  <option key={offerName} value={offerName}>
                    {offerName}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <Button
              type="submit"
              primary
              disabled={isIssuingOffer || !selectedOffer}
            >
              {isIssuingOffer ? 'Issuing...' : 'Issue Perk'}
            </Button>
          </IssueForm>

          {message && (
            message.type === 'success' ?
              <SuccessMessage>{message.text}</SuccessMessage> :
              <ErrorMessage>{message.text}</ErrorMessage>
          )}
        </IssueOffersSection>
        )}

        {isAdmin && (
          <IssueOffersSection>
            <SectionTitle>Issue Gift Card</SectionTitle>
            <IssueForm onSubmit={(e) => { e.preventDefault(); handleGiftCardClick(); }}>
              <FormGroup>
                <Label htmlFor="giftCardMode">Gift Card Type</Label>
                <Select
                  id="giftCardMode"
                  value={giftCardMode}
                  onChange={(e) => {
                    setGiftCardMode(e.target.value as 'milestone' | 'custom')
                    setGiftCardMessage(null)
                  }}
                >
                  <option value="milestone">Milestone</option>
                  <option value="custom">Custom Gift Card</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="giftCardCurrency">Currency</Label>
                <Select
                  id="giftCardCurrency"
                  value={giftCardCurrency}
                  onChange={(e) => {
                    setGiftCardCurrency(e.target.value as 'USD' | 'GBP' | 'EUR')
                    setGiftCardMessage(null)
                  }}
                >
                  <option value="USD">$ USD (US Store)</option>
                  <option value="GBP">£ GBP (UK Store)</option>
                  <option value="EUR">€ EUR (Ireland Store)</option>
                </Select>
              </FormGroup>

              {giftCardMode === 'milestone' ? (
                <FormGroup>
                  <Label htmlFor="milestoneSelect">Select Milestone</Label>
                  <Select
                    id="milestoneSelect"
                    value={selectedMilestone}
                    onChange={(e) => setSelectedMilestone(e.target.value)}
                  >
                    <option value="">Choose a milestone...</option>
                    {Object.entries(GiftCardMilestones).map(([key, data]) => (
                      <option key={key} value={key}>
                        {data.label}
                      </option>
                    ))}
                  </Select>
                </FormGroup>
              ) : (
                <>
                  <FormGroup>
                    <Label htmlFor="customAmount">Amount</Label>
                    <AmountInputWrapper>
                      <CurrencySymbol>{currencySymbol}</CurrencySymbol>
                      <Input
                        id="customAmount"
                        type="number"
                        min="0.01"
                        step="0.01"
                        placeholder="0.00"
                        value={customAmount}
                        onChange={(e) => {
                          setCustomAmount(e.target.value)
                          setGiftCardMessage(null)
                        }}
                      />
                    </AmountInputWrapper>
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="customNote">Note</Label>
                    <TextArea
                      id="customNote"
                      placeholder="Enter gift card note..."
                      value={customNote}
                      onChange={(e) => setCustomNote(e.target.value)}
                    />
                    <ExampleText>*Red Room Records Finisher etc...</ExampleText>
                  </FormGroup>
                </>
              )}

              <Button
                type="submit"
                primary
                disabled={isIssuingGiftCard || (giftCardMode === 'milestone' ? !selectedMilestone : !customAmount)}
              >
                {isIssuingGiftCard ? 'Issuing...' : 'Issue Gift Card'}
              </Button>
            </IssueForm>

            {giftCardMessage && (
              giftCardMessage.type === 'success' ?
                <SuccessMessage>{giftCardMessage.text}</SuccessMessage> :
                <ErrorMessage>{giftCardMessage.text}</ErrorMessage>
            )}
          </IssueOffersSection>
        )}
      </IssueOffersContainer>

      <GiftCardHistorySection>
        <GiftCardHistoryHeader>
          <SectionTitle>Shopify Gift Cards</SectionTitle>
          <RefreshButton onClick={loadGiftCardHistory} disabled={isLoadingGiftCardHistory}>
            {isLoadingGiftCardHistory ? 'Loading...' : 'Refresh'}
          </RefreshButton>
        </GiftCardHistoryHeader>

        {isLoadingGiftCardHistory ? (
          <LoadingMessage>Loading gift card history...</LoadingMessage>
        ) : giftCardHistory.length > 0 ? (
          <GiftCardTable>
            <thead>
              <tr>
                <TableHeader>Code</TableHeader>
                <TableHeader>Store</TableHeader>
                <TableHeader>Initial Value</TableHeader>
                <TableHeader>Balance</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>From</TableHeader>
                <TableHeader>Note</TableHeader>
                <TableHeader>Created</TableHeader>
                <TableHeader>Actions</TableHeader>
              </tr>
            </thead>
            <tbody>
              {giftCardHistory.map((gc: any) => (
                <TableRow key={gc.id}>
                  <TableCell><CodeText>{isSuperAdmin && gc.full_code ? gc.full_code : `****${gc.code_last4}`}</CodeText></TableCell>
                  <TableCell>{gc.store || 'US'}</TableCell>
                  <TableCell>{gc.currency === 'GBP' ? '£' : gc.currency === 'EUR' ? '€' : '$'}{parseFloat(gc.initial_value).toFixed(2)}</TableCell>
                  <TableCell>{gc.currency === 'GBP' ? '£' : gc.currency === 'EUR' ? '€' : '$'}{parseFloat(gc.balance).toFixed(2)}</TableCell>
                  <TableCell>
                    <StatusBadge active={gc.status === 'active'}>
                      {gc.status}
                    </StatusBadge>
                  </TableCell>
                  <TableCell>
                    {gc.sender ? (
                      <span title={gc.sender.buyer_email || ''}>
                        {gc.sender.buyer_name || gc.sender.buyer_email || '-'}
                        {gc.sender.message && <><br /><small style={{ color: '#666' }}>"{gc.sender.message}"</small></>}
                      </span>
                    ) : '-'}
                  </TableCell>
                  <TableCell>{gc.note || '-'}</TableCell>
                  <TableCell>{new Date(gc.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    {gc.status === 'active' ? (
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => handleResendGiftCardNotification(gc.id, gc.currency)}
                          disabled={resendingGiftCardId === gc.id || !!gc._via}
                          title={gc._via ? "Resend requires a verified Shopify customer-owned card. This search match cannot be resent." : undefined}
                          style={{
                            background: '#1976d2',
                            color: 'white',
                            border: 'none',
                            padding: '0.4rem 0.8rem',
                            borderRadius: '4px',
                            fontSize: '1.1rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            opacity: resendingGiftCardId === gc.id ? 0.5 : 1,
                          }}
                        >
                          {gc._via ? 'Resend unavailable' : resendingGiftCardId === gc.id ? 'Sending...' : 'Resend'}
                        </button>
                        {isSuperAdmin && (
                          <button
                            onClick={() => handleDeactivateGiftCard(gc.id, gc.currency)}
                            disabled={deactivatingGiftCardId === gc.id}
                            style={{
                              background: '#c33',
                              color: 'white',
                              border: 'none',
                              padding: '0.4rem 0.8rem',
                              borderRadius: '4px',
                              fontSize: '1.1rem',
                              fontWeight: 600,
                              cursor: 'pointer',
                              opacity: deactivatingGiftCardId === gc.id ? 0.5 : 1,
                            }}
                          >
                            {deactivatingGiftCardId === gc.id ? 'Disabling...' : 'Deactivate'}
                          </button>
                        )}
                      </div>
                    ) : (
                      <span style={{ color: '#999', fontSize: '1.1rem' }}>-</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </GiftCardTable>
        ) : (
          <NoOffersMessage>No gift cards found for this user</NoOffersMessage>
        )}
      </GiftCardHistorySection>

      <GiftCardHistorySection>
        <GiftCardHistoryHeader>
          <SectionTitle>Purchased Mariana Tek Gift Cards</SectionTitle>
          <RefreshButton onClick={() => loadMtGiftCards()} disabled={isLoadingMtGiftCards}>
            {isLoadingMtGiftCards ? 'Loading...' : 'Refresh'}
          </RefreshButton>
        </GiftCardHistoryHeader>

        {isLoadingMtGiftCards ? (
          <LoadingMessage>Loading Mariana Tek gift cards...</LoadingMessage>
        ) : mtGiftCards.length > 0 ? (
          <GiftCardTable>
            <thead>
              <tr>
                <TableHeader>Recipient</TableHeader>
                <TableHeader>Message</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Purchased</TableHeader>
                <TableHeader>Redeemed</TableHeader>
              </tr>
            </thead>
            <tbody>
              {mtGiftCards.map((gc: any) => (
                <TableRow key={gc.id}>
                  <TableCell>
                    <span>{gc.attributes?.recipient_name || '-'}</span>
                    {gc.attributes?.recipient_email && (
                      <><br /><small style={{ color: '#666' }}>{gc.attributes.recipient_email}</small></>
                    )}
                  </TableCell>
                  <TableCell>
                    {gc.attributes?.custom_message || '-'}
                  </TableCell>
                  <TableCell>
                    <StatusBadge active={!!gc.attributes?.redemption_datetime}>
                      {gc.attributes?.redemption_datetime ? 'redeemed' : gc.attributes?.scheduled_datetime ? 'scheduled' : 'pending'}
                    </StatusBadge>
                  </TableCell>
                  <TableCell>
                    {gc.attributes?.purchase_datetime ? new Date(gc.attributes.purchase_datetime).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell>
                    {gc.attributes?.redemption_datetime ? new Date(gc.attributes.redemption_datetime).toLocaleDateString() : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </GiftCardTable>
        ) : (
          <NoOffersMessage>No Mariana Tek gift cards found for this user</NoOffersMessage>
        )}
      </GiftCardHistorySection>

      <GiftCardHistorySection>
        <GiftCardHistoryHeader>
          <SectionTitle>Received Mariana Tek Gift Cards</SectionTitle>
          <RefreshButton onClick={loadReceivedGiftCards} disabled={isLoadingReceivedGiftCards}>
            {isLoadingReceivedGiftCards ? 'Loading...' : 'Refresh'}
          </RefreshButton>
        </GiftCardHistoryHeader>

        {isLoadingReceivedGiftCards ? (
          <LoadingMessage>Searching for received gift cards (this may take a few seconds)...</LoadingMessage>
        ) : receivedGiftCards.length > 0 ? (
          <GiftCardTable>
            <thead>
              <tr>
                <TableHeader>Code</TableHeader>
                <TableHeader>From</TableHeader>
                <TableHeader>Product</TableHeader>
                <TableHeader>Value</TableHeader>
                <TableHeader>Message</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Purchased</TableHeader>
                <TableHeader>Redeemed By</TableHeader>
              </tr>
            </thead>
            <tbody>
              {receivedGiftCards.map((gc: any, index: number) => (
                <TableRow key={gc.gift_card_code || (gc.code_last4 ? `last4-${gc.code_last4}-${index}` : index)}>
                  <TableCell>
                    <CodeText>
                      {gc.gift_card_code
                        ? maskCode(gc.gift_card_code, isSuperAdmin)
                        : gc.code_last4 ? `****${gc.code_last4}` : '-'}
                    </CodeText>
                  </TableCell>
                  <TableCell>
                    <span>{gc.purchased_by || '-'}</span>
                    {gc.purchased_by_email && (
                      <><br /><small style={{ color: '#666' }}>{gc.purchased_by_email}</small></>
                    )}
                  </TableCell>
                  <TableCell>
                    <span>{gc.product || '-'}</span>
                    {gc.purchase_location && (
                      <><br /><small style={{ color: '#666' }}>{gc.purchase_location}</small></>
                    )}
                  </TableCell>
                  <TableCell>{gc.value ? `$${parseFloat(gc.value).toFixed(2)}` : '-'}</TableCell>
                  <TableCell>{gc.custom_message || '-'}</TableCell>
                  <TableCell>
                    <StatusBadge active={!!gc.redemption_date}>
                      {gc.redemption_date ? 'redeemed' : gc.send_type === 'Scheduled' ? 'scheduled' : 'pending'}
                    </StatusBadge>
                  </TableCell>
                  <TableCell>
                    {gc.date_sold ? new Date(gc.date_sold).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell>
                    {gc.redemption_broker ? (
                      <span>
                        {gc.redemption_broker}
                        {gc.redemption_broker_email && (
                          <><br /><small style={{ color: '#666' }}>{gc.redemption_broker_email}</small></>
                        )}
                      </span>
                    ) : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </GiftCardTable>
        ) : (
          <NoOffersMessage>No received MT gift cards found for this user</NoOffersMessage>
        )}
      </GiftCardHistorySection>

      {showGiftCardConfirmation && (
        <ModalOverlay onClick={handleCancelGiftCard}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Confirm Gift Card Issuance</ModalTitle>
            {giftCardMode === 'milestone' ? (
              <p>Are you sure you want to issue a <strong>{GiftCardMilestones[selectedMilestone]?.label}</strong> gift card?</p>
            ) : (
              <div>
                <p>Are you sure you want to issue a <strong>{currencySymbol}{parseFloat(customAmount).toFixed(2)}</strong> custom gift card?</p>
                {customNote && <p>Note: {customNote}</p>}
                {giftCardCurrency !== 'USD' && <p>Store: {giftCardCurrency === 'GBP' ? 'UK' : 'Ireland'} ({giftCardCurrency})</p>}
              </div>
            )}
            <ModalButtons>
              <Button onClick={handleCancelGiftCard}>
                Cancel
              </Button>
              <Button primary onClick={handleConfirmGiftCard}>
                Yes
              </Button>
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}

      <UserNotesModal
        isOpen={showUserNotes}
        onClose={() => setShowUserNotes(false)}
        userData={userData}
      />
    </OffersContainer>
  )
}

export default OffersSection
